import React, { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { Checkbox } from '@dashboard/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@dashboard/components/ui/popover';

export type TicketTypeOption = {
  id: string;
  name: string;
  groupName?: string;
};

interface Props {
  /** All ticket types available on this event, in display order. */
  options: TicketTypeOption[];
  /** Currently restricted-to ids. Empty array = unrestricted (all types). */
  value: string[];
  /** Fires whenever the user toggles a checkbox. */
  onChange: (next: string[]) => void;
  /** Label for the popover trigger; defaults to "Permissions". */
  triggerLabel?: string;
  /** Compact trigger styled like a table cell (used in the Permissions column). */
  inline?: boolean;
  disabled?: boolean;
}

const TicketTypeRestriction: React.FC<Props> = ({
  options,
  value,
  onChange,
  triggerLabel = 'Permissions',
  inline = false,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const valueSet = useMemo(() => new Set(value), [value]);
  const unrestricted = value.length === 0;

  const summary = useMemo(() => {
    if (unrestricted) return 'All';
    const picked = options.filter(o => valueSet.has(o.id)).map(o => o.name);
    if (picked.length === 0) return 'All';
    if (picked.length <= 2) return picked.join(', ');
    return `${picked.slice(0, 2).join(', ')} +${picked.length - 2}`;
  }, [unrestricted, options, valueSet]);

  const toggle = (id: string) => {
    const next = new Set(valueSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Keep the array in `options` order so the summary reads naturally.
    onChange(options.filter(o => next.has(o.id)).map(o => o.id));
  };

  const clear = () => onChange([]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={inline ? 'ghost' : 'outline'}
          size="sm"
          disabled={disabled}
          className={
            inline
              ? 'h-7 px-2 text-sm text-muted-foreground hover:text-foreground'
              : 'justify-between'
          }
        >
          <span className={inline ? '' : 'font-medium'}>{summary}</span>
          <ChevronDown className="size-3.5 ml-1.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          Choose which ticket types this staff member can scan.
        </div>
        <label className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-muted/60">
          <Checkbox checked={unrestricted} onCheckedChange={() => clear()} />
          <span className="text-sm font-medium">All</span>
        </label>
        <div className="border-t border-border my-1" />
        {options.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">
            This event has no ticket types yet.
          </div>
        ) : (
          options.map(opt => (
            <label
              key={opt.id}
              className="flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-muted/60"
            >
              <Checkbox
                className="mt-0.5"
                checked={valueSet.has(opt.id)}
                onCheckedChange={() => toggle(opt.id)}
              />
              <span className="text-sm leading-tight">
                <span className="font-medium">{opt.name}</span>
                {opt.groupName && (
                  <span className="block text-xs text-muted-foreground">{opt.groupName}</span>
                )}
              </span>
            </label>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TicketTypeRestriction;

// Helper: extract a flat ticket-type option list from a Page document.
// Hoisted so the wizard's disclosures, RecipientRows, and the Manage
// staff Permissions column all derive options the same way.
export function deriveTicketTypeOptions(page: any): TicketTypeOption[] {
  const groups: any[] = page?.ticketGroups ?? [];
  const showGroupName = groups.length > 1;
  return groups.flatMap(g =>
    (g.ticketTypes ?? []).map((t: any) => ({
      id: String(t._id),
      name: t.name ?? 'Untitled ticket type',
      groupName: showGroupName ? g.name : undefined,
    })),
  );
}
